import React, { useEffect, useState } from "react";
import { AgGridReact } from "ag-grid-react";
import { toast } from "react-toastify";
import Swal from "sweetalert2";
import moment from "moment";
import {
    listBackups,
    createBackup,
    downloadBackup,
    deleteBackup,
    restoreBackup,
    listBackupSchedules,
    createBackupSchedule,
    updateBackupSchedule,
    deleteBackupSchedule,
    getDatabaseTables,
} from "../../../services/backup";

const emptyBackupForm = { backup_name: "", backup_type: "full", tables: [] };
const emptyScheduleForm = {
    id: null,
    schedule_name: "",
    frequency: "daily",
    scheduled_time: "02:00",
    day_of_week: 0,
    day_of_month: 1,
    backup_type: "full",
    tables_to_backup: [],
    retention_days: 30,
    is_active: true,
};

export const BackupManagement = ({ embedded = false }) => {
    const [activeTab, setActiveTab] = useState("backups");
    const [backups, setBackups] = useState([]);
    const [schedules, setSchedules] = useState([]);
    const [tables, setTables] = useState([]);
    const [loading, setLoading] = useState(false);
    const [showBackupModal, setShowBackupModal] = useState(false);
    const [showScheduleModal, setShowScheduleModal] = useState(false);
    const [editMode, setEditMode] = useState(false);

    const [backupForm, setBackupForm] = useState(emptyBackupForm);
    const [scheduleForm, setScheduleForm] = useState(emptyScheduleForm);

    useEffect(() => {
        loadBackups();
        loadSchedules();
        loadTables();
    }, []);

    const loadBackups = async () => {
        const res = await listBackups({});
        if (res && res.status === true) {
            setBackups(res.data || []);
        }
    };

    const loadSchedules = async () => {
        const res = await listBackupSchedules();
        if (res && res.status === true) {
            setSchedules(res.data || []);
        }
    };

    const loadTables = async () => {
        const res = await getDatabaseTables();
        if (res && res.status === true) {
            setTables(res.data || []);
        }
    };

    const handleCreateBackup = async () => {
        if (!backupForm.backup_name) {
            toast.error("Please enter backup name");
            return;
        }
        if (backupForm.backup_type === "partial" && backupForm.tables.length === 0) {
            toast.error("Please select tables for partial backup");
            return;
        }

        setLoading(true);
        const res = await createBackup(backupForm);
        setLoading(false);
        if (res && res.status === true) {
            toast.success(res.message || "Backup created successfully");
            setShowBackupModal(false);
            setBackupForm(emptyBackupForm);
            loadBackups();
        }
    };

    const handleDownloadBackup = async (id, fileName) => {
        const res = await downloadBackup(id);
        if (res && res.data) {
            const url = window.URL.createObjectURL(new Blob([res.data]));
            const link = document.createElement("a");
            link.href = url;
            link.setAttribute("download", fileName);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        }
    };

    const handleRestoreBackup = async (id, backupName) => {
        const result = await Swal.fire({
            title: "Are you sure?",
            html: `<strong>WARNING:</strong> This will restore the database from backup <strong>${backupName}</strong>.<br><br>All current data will be replaced!`,
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#d33",
            cancelButtonColor: "#3085d6",
            confirmButtonText: "Yes, restore it!",
            input: "text",
            inputPlaceholder: "Type RESTORE to confirm",
            inputValidator: (value) => {
                if (value !== "RESTORE") {
                    return "You must type RESTORE to confirm";
                }
            },
        });

        if (!result.isConfirmed) return;

        setLoading(true);
        const res = await restoreBackup(id);
        setLoading(false);
        if (res && res.status === true) {
            toast.success(res.message || "Database restored successfully");
        }
    };

    const handleDeleteBackup = async (id) => {
        const result = await Swal.fire({
            title: "Are you sure?",
            text: "This backup will be deleted permanently",
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#d33",
            cancelButtonColor: "#3085d6",
            confirmButtonText: "Yes, delete it!",
        });

        if (!result.isConfirmed) return;

        const res = await deleteBackup(id);
        if (res && res.status === true) {
            toast.success(res.message || "Backup deleted successfully");
            loadBackups();
        }
    };

    const handleSaveSchedule = async () => {
        if (!scheduleForm.schedule_name) {
            toast.error("Please enter schedule name");
            return;
        }

        setLoading(true);
        const res = editMode
            ? await updateBackupSchedule(scheduleForm.id, scheduleForm)
            : await createBackupSchedule(scheduleForm);
        setLoading(false);
        if (res && res.status === true) {
            toast.success(`Schedule ${editMode ? "updated" : "created"} successfully`);
            setShowScheduleModal(false);
            loadSchedules();
        }
    };

    const handleEditSchedule = (schedule) => {
        setScheduleForm({
            id: schedule.id,
            schedule_name: schedule.schedule_name,
            frequency: schedule.frequency,
            scheduled_time: schedule.scheduled_time
                ? schedule.scheduled_time.substring(0, 5)
                : "02:00",
            day_of_week: schedule.day_of_week || 0,
            day_of_month: schedule.day_of_month || 1,
            backup_type: schedule.backup_type,
            tables_to_backup: schedule.tables_to_backup
                ? JSON.parse(schedule.tables_to_backup)
                : [],
            retention_days: schedule.retention_days,
            is_active: schedule.is_active,
        });
        setEditMode(true);
        setShowScheduleModal(true);
    };

    const handleDeleteSchedule = async (id) => {
        const result = await Swal.fire({
            title: "Are you sure?",
            text: "This schedule will be deleted",
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#d33",
            cancelButtonColor: "#3085d6",
            confirmButtonText: "Yes, delete it!",
        });

        if (!result.isConfirmed) return;

        const res = await deleteBackupSchedule(id);
        if (res && res.status === true) {
            toast.success(res.message || "Schedule deleted successfully");
            loadSchedules();
        }
    };

    const statusBadge = (status) => {
        const badges = {
            in_progress: "bg-warning",
            completed: "bg-success",
            failed: "bg-danger",
        };
        return badges[status] || "bg-secondary";
    };

    const toggleTable = (form, setForm, key, table, checked) => {
        if (checked) {
            setForm({ ...form, [key]: [...form[key], table] });
        } else {
            setForm({ ...form, [key]: form[key].filter((t) => t !== table) });
        }
    };

    const backupColumns = [
        {
            headerName: "Backup Name",
            field: "backup_name",
            sortable: true,
            resizable: true,
            filter: true,
            floatingFilter: true,
            cellStyle: { textAlign: "left" },
        },
        {
            headerName: "Type",
            field: "backup_type",
            width: 110,
            sortable: true,
            resizable: true,
            filter: true,
            cellStyle: { textAlign: "left" },
            cellRenderer: (params) => (
                <span className="badge bg-info text-capitalize">{params.value}</span>
            ),
        },
        {
            headerName: "File Name",
            field: "file_name",
            sortable: true,
            resizable: true,
            filter: true,
            floatingFilter: true,
            cellStyle: { textAlign: "left" },
        },
        {
            headerName: "Size",
            field: "file_size_human",
            width: 110,
            sortable: true,
            resizable: true,
            cellStyle: { textAlign: "left" },
        },
        {
            headerName: "Status",
            field: "status",
            width: 130,
            sortable: true,
            resizable: true,
            filter: true,
            cellStyle: { textAlign: "left" },
            cellRenderer: (params) => (
                <span className={`badge ${statusBadge(params.value)} text-capitalize`}>
                    {params.value ? params.value.replace("_", " ") : "-"}
                </span>
            ),
        },
        {
            headerName: "Created By",
            field: "created_by",
            width: 130,
            sortable: true,
            resizable: true,
            filter: true,
            cellStyle: { textAlign: "left" },
        },
        {
            headerName: "Started At",
            field: "started_at",
            width: 170,
            sortable: true,
            resizable: true,
            cellStyle: { textAlign: "left" },
            valueFormatter: (params) =>
                params.value ? moment(params.value).format("DD-MMM-YYYY HH:mm") : "-",
        },
        {
            headerName: "Actions",
            field: "id",
            width: 190,
            sortable: false,
            filter: false,
            cellStyle: { textAlign: "left" },
            cellRenderer: (params) => (
                <div>
                    {params.data.status === "completed" && (
                        <>
                            <button
                                className="btn btn-sm btn-success me-1"
                                onClick={() =>
                                    handleDownloadBackup(params.value, params.data.file_name)
                                }
                                title="Download"
                            >
                                <i className="fa fa-download"></i>
                            </button>
                            <button
                                className="btn btn-sm btn-warning me-1"
                                onClick={() =>
                                    handleRestoreBackup(params.value, params.data.backup_name)
                                }
                                title="Restore"
                            >
                                <i className="fa fa-undo"></i>
                            </button>
                        </>
                    )}
                    <button
                        className="btn btn-sm btn-danger"
                        onClick={() => handleDeleteBackup(params.value)}
                        title="Delete"
                    >
                        <i className="fa fa-trash"></i>
                    </button>
                </div>
            ),
        },
    ];

    const body = (
        <>
                <div className="row">
                    <div className="col-xl-12">
                        <div className="card">
                            <div className="card-body">
                                <div className="tabs-container mb-3">
                                    <div className="tabs-menu">
                                        <button
                                            className={`tab-button ${activeTab === "backups" ? "active" : ""}`}
                                            onClick={() => setActiveTab("backups")}
                                        >
                                            <i className="fa fa-archive"></i> Backups
                                        </button>
                                        <button
                                            className={`tab-button ${activeTab === "schedules" ? "active" : ""}`}
                                            onClick={() => setActiveTab("schedules")}
                                        >
                                            <i className="fa fa-clock-o"></i> Schedules
                                        </button>
                                    </div>
                                </div>

                                {activeTab === "backups" && (
                                    <div>
                                        <div className="mb-3">
                                            <button
                                                className="btn btn-success"
                                                onClick={() => {
                                                    setBackupForm(emptyBackupForm);
                                                    setShowBackupModal(true);
                                                }}
                                            >
                                                <i className="fa fa-plus"></i> Create Backup
                                            </button>
                                        </div>

                                        <div className="ag-theme-alpine ag-style mt-2">
                                            <AgGridReact
                                                columnDefs={backupColumns}
                                                rowData={backups}
                                                pagination="true"
                                                paginationPageSize="10"
                                                sortingOrder={["asc", "desc"]}
                                                suppressDragLeaveHidesColumns={true}
                                            />
                                        </div>
                                    </div>
                                )}

                                {activeTab === "schedules" && (
                                    <div>
                                        <div className="mb-3">
                                            <button
                                                className="btn btn-success"
                                                onClick={() => {
                                                    setScheduleForm(emptyScheduleForm);
                                                    setEditMode(false);
                                                    setShowScheduleModal(true);
                                                }}
                                            >
                                                <i className="fa fa-plus"></i> New Schedule
                                            </button>
                                        </div>

                                        <div className="table-responsive">
                                            <table className="table table-bordered table-hover">
                                                <thead>
                                                    <tr>
                                                        <th>Schedule Name</th>
                                                        <th>Frequency</th>
                                                        <th>Time</th>
                                                        <th>Type</th>
                                                        <th>Retention</th>
                                                        <th>Last Run</th>
                                                        <th>Next Run</th>
                                                        <th>Status</th>
                                                        <th>Actions</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {schedules.length === 0 && (
                                                        <tr>
                                                            <td colSpan="9" className="text-center">
                                                                No backup schedules configured
                                                            </td>
                                                        </tr>
                                                    )}
                                                    {schedules.map((schedule) => (
                                                        <tr key={schedule.id}>
                                                            <td>{schedule.schedule_name}</td>
                                                            <td className="text-capitalize">
                                                                {schedule.frequency}
                                                            </td>
                                                            <td>{schedule.scheduled_time}</td>
                                                            <td>
                                                                <span className="badge bg-info text-capitalize">
                                                                    {schedule.backup_type}
                                                                </span>
                                                            </td>
                                                            <td>{schedule.retention_days} days</td>
                                                            <td>
                                                                {schedule.last_run_at
                                                                    ? moment(schedule.last_run_at).format(
                                                                          "DD-MMM-YYYY HH:mm"
                                                                      )
                                                                    : "Never"}
                                                            </td>
                                                            <td>
                                                                {schedule.next_run_at
                                                                    ? moment(schedule.next_run_at).format(
                                                                          "DD-MMM-YYYY HH:mm"
                                                                      )
                                                                    : "-"}
                                                            </td>
                                                            <td>
                                                                <span
                                                                    className={`badge ${
                                                                        schedule.is_active
                                                                            ? "bg-success"
                                                                            : "bg-secondary"
                                                                    }`}
                                                                >
                                                                    {schedule.is_active
                                                                        ? "Active"
                                                                        : "Inactive"}
                                                                </span>
                                                            </td>
                                                            <td>
                                                                <button
                                                                    className="btn btn-sm btn-primary me-2"
                                                                    onClick={() => handleEditSchedule(schedule)}
                                                                >
                                                                    <i className="fa fa-edit"></i>
                                                                </button>
                                                                <button
                                                                    className="btn btn-sm btn-danger"
                                                                    onClick={() =>
                                                                        handleDeleteSchedule(schedule.id)
                                                                    }
                                                                >
                                                                    <i className="fa fa-trash"></i>
                                                                </button>
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Create Backup Modal */}
                {showBackupModal && (
                    <div
                        className="modal show d-block"
                        tabIndex="-1"
                        style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
                    >
                        <div className="modal-dialog modal-lg">
                            <div className="modal-content">
                                <div className="modal-header">
                                    <h5 className="modal-title">Create Backup</h5>
                                    <button
                                        type="button"
                                        className="btn-close"
                                        onClick={() => setShowBackupModal(false)}
                                    ></button>
                                </div>
                                <div className="modal-body">
                                    <div className="mb-3">
                                        <label className="form-label fw-bold">Backup Name *</label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            value={backupForm.backup_name}
                                            onChange={(e) =>
                                                setBackupForm({
                                                    ...backupForm,
                                                    backup_name: e.target.value,
                                                })
                                            }
                                            placeholder="e.g., Monthly Backup - July 2026"
                                        />
                                    </div>

                                    <div className="mb-3">
                                        <label className="form-label fw-bold">Backup Type *</label>
                                        <select
                                            className="form-control"
                                            value={backupForm.backup_type}
                                            onChange={(e) =>
                                                setBackupForm({
                                                    ...backupForm,
                                                    backup_type: e.target.value,
                                                })
                                            }
                                        >
                                            <option value="full">Full Backup (All Tables)</option>
                                            <option value="partial">
                                                Partial Backup (Selected Tables)
                                            </option>
                                        </select>
                                    </div>

                                    {backupForm.backup_type === "partial" && (
                                        <div className="mb-3">
                                            <label className="form-label fw-bold">Select Tables *</label>
                                            <div
                                                style={{
                                                    maxHeight: "300px",
                                                    overflowY: "auto",
                                                    border: "1px solid #ddd",
                                                    padding: "10px",
                                                }}
                                            >
                                                {tables.map((table) => (
                                                    <div key={table} className="form-check">
                                                        <input
                                                            type="checkbox"
                                                            className="form-check-input"
                                                            id={`table-${table}`}
                                                            checked={backupForm.tables.includes(table)}
                                                            onChange={(e) =>
                                                                toggleTable(
                                                                    backupForm,
                                                                    setBackupForm,
                                                                    "tables",
                                                                    table,
                                                                    e.target.checked
                                                                )
                                                            }
                                                        />
                                                        <label
                                                            className="form-check-label"
                                                            htmlFor={`table-${table}`}
                                                        >
                                                            {table}
                                                        </label>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </div>
                                <div className="modal-footer">
                                    <button
                                        type="button"
                                        className="btn btn-secondary"
                                        onClick={() => setShowBackupModal(false)}
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="button"
                                        className="btn btn-primary"
                                        onClick={handleCreateBackup}
                                        disabled={loading}
                                    >
                                        <i className="fa fa-save"></i>{" "}
                                        {loading ? "Creating..." : "Create Backup"}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Schedule Modal */}
                {showScheduleModal && (
                    <div
                        className="modal show d-block"
                        tabIndex="-1"
                        style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
                    >
                        <div className="modal-dialog modal-lg">
                            <div className="modal-content">
                                <div className="modal-header">
                                    <h5 className="modal-title">
                                        {editMode ? "Edit Schedule" : "New Schedule"}
                                    </h5>
                                    <button
                                        type="button"
                                        className="btn-close"
                                        onClick={() => setShowScheduleModal(false)}
                                    ></button>
                                </div>
                                <div className="modal-body">
                                    <div className="row mb-3">
                                        <div className="col-md-6">
                                            <label className="form-label fw-bold">
                                                Schedule Name *
                                            </label>
                                            <input
                                                type="text"
                                                className="form-control"
                                                value={scheduleForm.schedule_name}
                                                onChange={(e) =>
                                                    setScheduleForm({
                                                        ...scheduleForm,
                                                        schedule_name: e.target.value,
                                                    })
                                                }
                                            />
                                        </div>
                                        <div className="col-md-6">
                                            <label className="form-label fw-bold">Frequency *</label>
                                            <select
                                                className="form-control"
                                                value={scheduleForm.frequency}
                                                onChange={(e) =>
                                                    setScheduleForm({
                                                        ...scheduleForm,
                                                        frequency: e.target.value,
                                                    })
                                                }
                                            >
                                                <option value="daily">Daily</option>
                                                <option value="weekly">Weekly</option>
                                                <option value="monthly">Monthly</option>
                                            </select>
                                        </div>
                                    </div>

                                    <div className="row mb-3">
                                        <div className="col-md-4">
                                            <label className="form-label fw-bold">Time *</label>
                                            <input
                                                type="time"
                                                className="form-control"
                                                value={scheduleForm.scheduled_time}
                                                onChange={(e) =>
                                                    setScheduleForm({
                                                        ...scheduleForm,
                                                        scheduled_time: e.target.value,
                                                    })
                                                }
                                            />
                                        </div>

                                        {scheduleForm.frequency === "weekly" && (
                                            <div className="col-md-4">
                                                <label className="form-label fw-bold">
                                                    Day of Week *
                                                </label>
                                                <select
                                                    className="form-control"
                                                    value={scheduleForm.day_of_week}
                                                    onChange={(e) =>
                                                        setScheduleForm({
                                                            ...scheduleForm,
                                                            day_of_week: parseInt(e.target.value),
                                                        })
                                                    }
                                                >
                                                    <option value="0">Sunday</option>
                                                    <option value="1">Monday</option>
                                                    <option value="2">Tuesday</option>
                                                    <option value="3">Wednesday</option>
                                                    <option value="4">Thursday</option>
                                                    <option value="5">Friday</option>
                                                    <option value="6">Saturday</option>
                                                </select>
                                            </div>
                                        )}

                                        {scheduleForm.frequency === "monthly" && (
                                            <div className="col-md-4">
                                                <label className="form-label fw-bold">
                                                    Day of Month *
                                                </label>
                                                <input
                                                    type="number"
                                                    className="form-control"
                                                    min="1"
                                                    max="31"
                                                    value={scheduleForm.day_of_month}
                                                    onChange={(e) =>
                                                        setScheduleForm({
                                                            ...scheduleForm,
                                                            day_of_month: parseInt(e.target.value),
                                                        })
                                                    }
                                                />
                                            </div>
                                        )}

                                        <div className="col-md-4">
                                            <label className="form-label fw-bold">
                                                Retention (Days) *
                                            </label>
                                            <input
                                                type="number"
                                                className="form-control"
                                                min="1"
                                                value={scheduleForm.retention_days}
                                                onChange={(e) =>
                                                    setScheduleForm({
                                                        ...scheduleForm,
                                                        retention_days: parseInt(e.target.value),
                                                    })
                                                }
                                            />
                                        </div>
                                    </div>

                                    <div className="row mb-3">
                                        <div className="col-md-6">
                                            <label className="form-label fw-bold">Backup Type *</label>
                                            <select
                                                className="form-control"
                                                value={scheduleForm.backup_type}
                                                onChange={(e) =>
                                                    setScheduleForm({
                                                        ...scheduleForm,
                                                        backup_type: e.target.value,
                                                    })
                                                }
                                            >
                                                <option value="full">Full Backup</option>
                                                <option value="partial">Partial Backup</option>
                                            </select>
                                        </div>
                                        <div className="col-md-6">
                                            <label className="form-label fw-bold">Status</label>
                                            <div className="form-check form-switch mt-2">
                                                <input
                                                    type="checkbox"
                                                    className="form-check-input"
                                                    checked={scheduleForm.is_active}
                                                    onChange={(e) =>
                                                        setScheduleForm({
                                                            ...scheduleForm,
                                                            is_active: e.target.checked,
                                                        })
                                                    }
                                                />
                                                <label className="form-check-label">
                                                    {scheduleForm.is_active ? "Active" : "Inactive"}
                                                </label>
                                            </div>
                                        </div>
                                    </div>

                                    {scheduleForm.backup_type === "partial" && (
                                        <div className="mb-3">
                                            <label className="form-label fw-bold">Select Tables *</label>
                                            <div
                                                style={{
                                                    maxHeight: "250px",
                                                    overflowY: "auto",
                                                    border: "1px solid #ddd",
                                                    padding: "10px",
                                                }}
                                            >
                                                {tables.map((table) => (
                                                    <div key={table} className="form-check">
                                                        <input
                                                            type="checkbox"
                                                            className="form-check-input"
                                                            id={`sched-table-${table}`}
                                                            checked={scheduleForm.tables_to_backup.includes(
                                                                table
                                                            )}
                                                            onChange={(e) =>
                                                                toggleTable(
                                                                    scheduleForm,
                                                                    setScheduleForm,
                                                                    "tables_to_backup",
                                                                    table,
                                                                    e.target.checked
                                                                )
                                                            }
                                                        />
                                                        <label
                                                            className="form-check-label"
                                                            htmlFor={`sched-table-${table}`}
                                                        >
                                                            {table}
                                                        </label>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </div>
                                <div className="modal-footer">
                                    <button
                                        type="button"
                                        className="btn btn-secondary"
                                        onClick={() => setShowScheduleModal(false)}
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="button"
                                        className="btn btn-primary"
                                        onClick={handleSaveSchedule}
                                        disabled={loading}
                                    >
                                        <i className="fa fa-save"></i> Save Schedule
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
        </>
    );

    if (embedded) {
        return body;
    }

    return (
        <div className="app-content main-content">
            <div className="side-app main-container">
                <div className="page-header d-xl-flex d-block">
                    <div className="page-leftheader">
                        <div className="page-title">
                            <i className="fa fa-database me-2"></i> Data Backup &amp; Recovery
                        </div>
                    </div>
                </div>
                {body}
            </div>
        </div>
    );
};

export default BackupManagement;
